import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  Dialog,
  Field,
  Flex,
  Heading,
  IconButton,
  Input,
  NativeSelect,
  Portal,
  Separator,
  Stack,
  Text,
} from '@chakra-ui/react'
import { LuPencil, LuPlus, LuTrash2, LuWallet, LuX } from 'react-icons/lu'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import { useUpdateEventFinancial } from '@/hooks/events/useUpdateEventFinancial'
import { useCreateEventExpense } from '@/hooks/events/useCreateEventExpense'
import { useUpdateEventExpense } from '@/hooks/events/useUpdateEventExpense'
import { useDeleteEventExpense } from '@/hooks/events/useDeleteEventExpense'
import { useExpenseCategories } from '@/hooks/agendas/useExpenseCategories'
import { formatCurrency, parseCurrencyToNumber } from '@/utils/currency'
import CurrencyField from '@/components/CurrencyField'
import ConfirmActionDialog from '@/components/ConfirmActionDialog'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import type { ApiErrorResponse } from '@/lib/api'
import type { EventDetail, EventExpense, EventFinancial } from '@/services/events'

type Props = {
  event: EventDetail
  agendaId: string
  eventId: string
  canEditFinancial: boolean
}

// ─── Edit cachê form ─────────────────────────────────────────────────────────

type EditCacheFormProps = {
  financial: EventFinancial | null
  onCancel: () => void
  agendaId: string
  eventId: string
}

const EditCacheForm = ({ financial, onCancel, agendaId, eventId }: EditCacheFormProps) => {
  const [cacheValueStr, setCacheValueStr] = useState(formatCurrency(financial?.cache_value ?? 0))

  const mutation = useUpdateEventFinancial(agendaId, eventId)
  const validationErrors = (mutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data
    ?.validation_errors

  const handleCancel = () => {
    mutation.reset()
    onCancel()
  }

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault()
    mutation.mutate(
      { cache_value: parseCurrencyToNumber(cacheValueStr) },
      {
        onSuccess: () => {
          showSuccess('Financeiro atualizado com sucesso!')
          onCancel()
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={4}>
        <CurrencyField
          label="Valor do Cachê"
          value={cacheValueStr}
          onChange={setCacheValueStr}
          errors={validationErrors?.cache_value}
        />
        <Flex gap={2} justify="flex-end">
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={mutation.isPending}>
            <LuX />
            Cancelar
          </Button>
          <Button type="submit" colorPalette="primary" size="sm" loading={mutation.isPending}>
            Salvar
          </Button>
        </Flex>
      </Stack>
    </form>
  )
}

// ─── Expense form dialog ──────────────────────────────────────────────────────

type ExpenseFormDialogProps = {
  agendaId: string
  eventId: string
  expense?: EventExpense
  onClose: () => void
}

const ExpenseFormDialog = ({ agendaId, eventId, expense, onClose }: ExpenseFormDialogProps) => {
  const isEditing = !!expense

  const [amountStr, setAmountStr] = useState(formatCurrency(expense?.amount ?? 0))

  const { data: categories = [] } = useExpenseCategories(agendaId)

  const createMutation = useCreateEventExpense(agendaId, eventId)
  const updateMutation = useUpdateEventExpense(agendaId, eventId)
  const mutation = isEditing ? updateMutation : createMutation

  const validationErrors = (mutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data
    ?.validation_errors

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      mutation.reset()
      onClose()
    }
  }

  const handleSubmit = (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const payload = {
      amount: parseCurrencyToNumber(amountStr),
      description: (form.get('description') as string).trim(),
      category_id: Number(form.get('category_id')),
    }

    if (isEditing) {
      updateMutation.mutate(
        { expenseId: expense.id, payload },
        {
          onSuccess: () => {
            showSuccess('Despesa atualizada com sucesso!')
            onClose()
          },
        },
      )
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          showSuccess('Despesa adicionada com sucesso!')
          onClose()
        },
      })
    }
  }

  return (
    <Dialog.Root
      size="sm"
      placement="center"
      open
      onOpenChange={({ open }) => handleOpenChange(open)}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content m={6}>
            <form onSubmit={handleSubmit}>
              <Dialog.Header>
                <Dialog.Title>{isEditing ? 'Editar despesa' : 'Nova despesa'}</Dialog.Title>
              </Dialog.Header>

              <Dialog.Body>
                <Stack gap={4}>
                  <Field.Root required invalid={!!validationErrors?.category_id}>
                    <Field.Label>
                      Categoria
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <NativeSelect.Root variant="subtle">
                      <NativeSelect.Field
                        name="category_id"
                        defaultValue={expense?.category.id ?? ''}
                      >
                        <option value="" disabled>Selecione uma categoria</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    {validationErrors?.category_id?.map((error, i) => (
                      <Field.ErrorText key={i}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <Field.Root required invalid={!!validationErrors?.description}>
                    <Field.Label>
                      Descrição
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input
                      name="description"
                      placeholder="Ex: Alimentação da equipe"
                      variant="subtle"
                      defaultValue={expense?.description ?? ''}
                    />
                    {validationErrors?.description?.map((error, i) => (
                      <Field.ErrorText key={i}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <CurrencyField
                    label="Valor"
                    value={amountStr}
                    onChange={setAmountStr}
                    errors={validationErrors?.amount}
                  />
                </Stack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={mutation.isPending}
                >
                  Cancelar
                </Button>
                <Button size="sm" type="submit" colorPalette="primary" loading={mutation.isPending}>
                  {isEditing ? 'Salvar' : 'Adicionar'}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

// ─── Expense row ──────────────────────────────────────────────────────────────

type ExpenseRowProps = {
  expense: EventExpense
  agendaId: string
  eventId: string
  canEditFinancial: boolean
}

const ExpenseRow = ({ expense, agendaId, eventId, canEditFinancial }: ExpenseRowProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const deleteMutation = useDeleteEventExpense(agendaId, eventId)

  return (
    <>
      <Flex
        align={{ base: 'flex-start', sm: 'center' }}
        justify="space-between"
        gap={3}
        py={2}
        px={3}
        borderWidth="1px"
        borderRadius="md"
        borderColor="border.muted"
      >
        <Box flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="medium" truncate>{expense.description}</Text>
          <Text fontSize="xs" color="fg.muted">{expense.category.name}</Text>
          <Text fontSize="sm" fontWeight="semibold" color="red.600" display={{ base: 'block', sm: 'none' }} mt={1}>
            -{formatCurrency(expense.amount)}
          </Text>
        </Box>

        <Flex align="center" gap={3} flexShrink={0}>
          <Text fontSize="sm" fontWeight="semibold" color="red.600" display={{ base: 'none', sm: 'block' }}>
            -{formatCurrency(expense.amount)}
          </Text>
          {canEditFinancial && (
            <Flex gap={1}>
              <IconButton
                size="xs"
                variant="ghost"
                colorPalette="yellow"
                color="yellow.600"
                aria-label="Editar despesa"
                onClick={() => setIsEditing(true)}
                disabled={deleteMutation.isPending}
              >
                <LuPencil />
              </IconButton>
              <IconButton
                size="xs"
                variant="ghost"
                colorPalette="red"
                color="red.600"
                aria-label="Remover despesa"
                onClick={() => setConfirmDelete(true)}
                loading={deleteMutation.isPending}
              >
                <LuTrash2 />
              </IconButton>
            </Flex>
          )}
        </Flex>
      </Flex>

      {isEditing && (
        <ExpenseFormDialog
          agendaId={agendaId}
          eventId={eventId}
          expense={expense}
          onClose={() => setIsEditing(false)}
        />
      )}

      {confirmDelete && (
        <ConfirmActionDialog
          open
          onOpenChange={(open) => { if (!open) setConfirmDelete(false) }}
          title="Remover despesa"
          body={<>Tem certeza que deseja remover a despesa <strong>{expense.description}</strong>?</>}
          onConfirm={() =>
            deleteMutation.mutate(expense.id, {
              onSuccess: () => {
                showSuccess('Despesa removida com sucesso!')
                setConfirmDelete(false)
              },
            })
          }
          confirmLabel="Remover"
          confirmColorPalette="red"
          isPending={deleteMutation.isPending}
        />
      )}
    </>
  )
}

// ─── Main section ─────────────────────────────────────────────────────────────

const FinancialSection = ({ event, agendaId, eventId, canEditFinancial }: Props) => {
  const [isEditingCache, setIsEditingCache] = useState(false)
  const [isAddingExpense, setIsAddingExpense] = useState(false)

  const financial = event.financial

  return (
    <Card.Root>
      <Card.Body>
        <Flex justify="space-between" align="flex-start" mb={4}>
          <Flex align="center" gap={3}>
            <Box
              bg="green.subtle"
              color="green.600"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <LuWallet size={16} />
            </Box>
            <Box>
              <Heading size="sm" fontWeight="semibold">Composição Financeira</Heading>
              <Text fontSize="xs" color="fg.muted">Cachê e despesas do evento</Text>
            </Box>
          </Flex>
          {!isEditingCache && canEditFinancial && (
            <Button size="xs" variant="ghost" onClick={() => setIsEditingCache(true)} aria-label="Editar cachê">
              <LuPencil />
            </Button>
          )}
        </Flex>

        {isEditingCache ? (
          <EditCacheForm
            financial={financial}
            onCancel={() => setIsEditingCache(false)}
            agendaId={agendaId}
            eventId={eventId}
          />
        ) : (
          <Stack gap={5}>
            {/* Totais */}
            <Flex gap={4} flexDir={{ base: 'column', md: 'row' }}>
              <Box flex="1">
                <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                  Cachê
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {formatCurrency(financial?.cache_value ?? 0)}
                </Text>
              </Box>

              <Box flex="1" borderLeft={{ sm: '1px solid' }} borderColor="border.subtle" pl={{ sm: 4 }}>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                  Total de Despesas
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="red.600">
                  -{formatCurrency(financial?.total_expenses ?? 0)}
                </Text>
              </Box>

              <Box flex="1" borderLeft={{ sm: '1px solid' }} borderColor="border.subtle" pl={{ sm: 4 }}>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                  Resultado Final
                </Text>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color={(financial?.final_result ?? 0) >= 0 ? 'green.600' : 'red.600'}
                >
                  {formatCurrency(financial?.final_result ?? 0)}
                </Text>
              </Box>
            </Flex>

            <Separator />

            {/* Despesas */}
            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontSize="sm" fontWeight="semibold">Despesas</Text>
                {canEditFinancial && (
                  <Button size="xs" variant="ghost" color="primary.500" fontWeight={600} onClick={() => setIsAddingExpense(true)}>
                    <LuPlus />
                    Adicionar
                  </Button>
                )}
              </Flex>

              <Stack gap={2}>
                {!financial || financial.expenses.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">Nenhuma despesa registrada.</Text>
                ) : (
                  financial.expenses.map((expense) => (
                    <ExpenseRow
                      key={expense.id}
                      expense={expense}
                      agendaId={agendaId}
                      eventId={eventId}
                      canEditFinancial={canEditFinancial}
                    />
                  ))
                )}
              </Stack>
            </Box>
          </Stack>
        )}

        {isAddingExpense && (
          <ExpenseFormDialog
            agendaId={agendaId}
            eventId={eventId}
            onClose={() => setIsAddingExpense(false)}
          />
        )}
      </Card.Body>
    </Card.Root>
  )
}

export default FinancialSection
